<template>
  <div
    class="group px-18 cursor-pointer rounded-10 anim-color"
    :class="isDarkMode ? 'hover:bg-project' : 'hover:bg-grey-200'"
    @click="openModal"
  >
    <div class="py-20 flex justify-between"
         :class="[isDarkMode ? 'border-sidebar' : 'border-grey-125',
                  {'border-t ': hasBorder}]"
    >
      <div class="pr-10 w-full">
        <!-- title -->
        <p
          class="font-bold text-20 anim-opacity opacity-80 group-hover:opacity-100 w-full leading-tight mb-5"
          :class="isDarkMode ? 'text-white' : 'text-black'"
        >
          <span v-if="discussion.isDraft" class="text-error">Draft</span>
          {{ discussion.title }}
        </p>

        <!-- content -->
        <div class="line-clamp hide-child text-14 opacity-70 group-hover:opacity-100 anim-opacity">
          {{ textContent }}
        </div>

        <!-- user, messages and members -->
        <div class="flex items-center mt-4">
          <div class="flex items-center">
            <div class="w-2 rounded-2 h-16" :style="userColor" />
            <p class="text-14 font-bold ml-7">{{ userName }}</p>
          </div>
          <span v-if="usersWhoReplied > 0" class="px-10 text-grey-250">•</span>
          <div v-if="countMessages > 0" class="flex items-center text-grey-250 mr-10">
            <i class="icon-messages mr-3" />
            <p class="text-14 leading-none">{{ countMessages }}</p>
          </div>
          <div v-if="usersWhoReplied > 0" class="text-grey-250 flex items-center">
            <i class="icon-members text-14 mr-2" />
            <span class="text-14">{{ usersWhoReplied }}</span>
          </div>
        </div>
      </div>

      <!-- menu and data -->
      <div class="flex flex-col flex-shrink-0">
        <DropdownModule
          v-if="isOwner"
          icon="icon-more-horizontal text-20"
          :width="150"
          top-position="30px"
          append-to-body
          class="md:opacity-0 group-hover:opacity-100 anim-opacity ml-auto"
          :class="{'opacity-100': showMenu}"
          remove-optional-top-gap
          :background="isDarkMode ? 'bg-sidebar' : 'bg-white'"
          is-auto-menu-position
          @click.stop="showMenu = true"
          @close="showMenu = false"
        >
          <div>
            <div class="hover:bg-selection rounded-t-10 group-scope text-15">
              <ButtonModule
                text="Edit"
                :prefix="`icon-edit mr-15 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
                class="p-10 w-full"
                @click="toggleEdit"
              />
            </div>
            <div class="hover:bg-selection group-scope text-15" :class="{'rounded-b-10': !discussion.resolvedAt}">
              <ButtonModule
                text="Delete"
                :prefix="`icon-delete mr-15 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
                class="p-10 w-full"
                @click="deleteDiscussion"
              />
            </div>
            <div v-if="discussion.resolvedAt" class="hover:bg-selection group-scope text-15 rounded-b-10">
              <ButtonModule
                text="Unresolve"
                :prefix="`icon-unresolve mr-15 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
                class="p-10 w-full"
                @click="unresolve"
              />
            </div>
          </div>
        </DropdownModule>
        <DiscussionModal
          v-if="modalIsOpen"
          :selected-book="selectedBook"
          :discussion="discussion"
          @close="closeModal"
        />

        <div class="text-14 text-grey-950" :title="titleTime">{{ getTime }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, ref } from 'vue'

import userColorMixin from '@/mixins/userColorMixin'
import { parseDate } from '@/plugins/filters'

import DiscussionModal from '@/components/discussions/DiscussionModal'
import DropdownModule from '@/components/shared/form-controls/DropdownModule'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import { useStore } from 'vuex'

export default defineComponent({
  components: {
    DiscussionModal,
    DropdownModule,
    ButtonModule
  },

  props: {
    discussion: Object,
    hasBorder: Boolean
  },

  setup (props) {
    const { state, getters, dispatch } = useStore()

    const showMenu = ref(false)
    const modalIsOpen = ref(false)

    const { userColor } = userColorMixin(props.discussion.user?.profile?.color)

    const isOwner = computed(() => props.discussion.user.id === state.profile.user.id)
    const userName = computed(() => props.discussion.user.profile.firstName + ' ' + props.discussion.user.profile.lastName)
    const countMessages = computed(() => props.discussion.discussionMessages?.length)
    const usersWhoReplied = computed(() => {
      const users = props.discussion.discussionMessages?.map(item => item.userId) || []
      const uniqueUsers = [...new Set(users)]
      return uniqueUsers.length
    })

    const isTwilightTheme = computed(() => getters.isTwilightTheme)
    const isDarkMode = computed(() => getters.isDarkMode)
    const headquarters = computed(() => state.team.headquarters)

    const textContent = computed(() => {
      var d = document.createElement('div')
      d.innerHTML = props.discussion.content?.replace(/(<([^>]+)>)/ig, '\n') || ''

      return d.textContent || d.innerText
    })

    const allBooks = computed(() => {
      const result = state.book.books.filter(b => !b.isSection)
      result.push(headquarters.value)
      return result
    })
    const selectedBook = computed(() => allBooks.value.find(b => b.id === parseInt(props.discussion.bookId)))

    const getTime = computed(() => {
      const minutes = (Date.now() - new Date(props.discussion.createdAt)) / 1000 / 60
      if (minutes < 1) {
        return 'now'
      } else if (minutes < 2) {
        return '1 minute ago'
      } else if (minutes < 60) {
        return `${parseInt(minutes)} minutes ago`
      } else if (minutes / 60 < 2) {
        return '1 hour ago'
      } else if (minutes / 60 < 24) {
        return `${parseInt(minutes / 60)} hours ago`
      } else if (minutes / 60 > 24) {
        return `${parseInt(minutes / 60 / 24)} days ago`
      }
      return parseDate(props.discussion.createdAt, 'DD-MM-YYYY')
    })

    const titleTime = computed(() => {
      return parseDate(props.discussion.createdAt, 'DD-MM-YYYY hh:mm')
    })

    function toggleEdit () {
      modalIsOpen.value = true
    }

    function closeModal () {
      modalIsOpen.value = false
    }

    function openModal () {
      if (props.discussion.isDraft) {
        modalIsOpen.value = true
      }
    }

    function deleteDiscussion () {
      dispatch('deleteDiscussion', {
        id: props.discussion.id,
        bookId: props.discussion.bookId
      })
    }

    function unresolve () {
      const message = props.discussion.discussionMessages.find(item => item.resolvedAt)
      dispatch('resolve', {
        messageId: message.id,
        discussionId: props.discussion.id,
        bookId: props.discussion.bookId,
        resolvedAt: null
      })
    }

    return {
      // ref

      // data
      showMenu,
      userColor,
      modalIsOpen,
      // computed
      getTime,
      isOwner,
      userName,
      titleTime,
      isDarkMode,
      textContent,
      selectedBook,
      countMessages,
      usersWhoReplied,
      isTwilightTheme,
      // function
      openModal,
      unresolve,
      toggleEdit,
      closeModal,
      deleteDiscussion
    }
  }
})
</script>
